"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

// BOOTH's one overlay shape: a sheet that rises from the bottom edge, the way
// Apple Pay's checkout does.
//
// It replaces the centred dialog for everything a visitor is asked to commit
// to — inquiring on a role, inquiring on a booth, signing up — because those
// all end in a single tap on a primary button, and a sheet puts that button at
// the edge the thumb already rests on instead of halfway up the screen. One
// component so the three never drift apart again.
//
// Built on the Radix primitives rather than ui/dialog: DialogContent
// hard-codes the centring transform, and overriding it from the outside is how
// you end up with a sheet that is centred on some breakpoints.
//
// Two tones, one anchoring. `ink` (the default) is the black surface the
// marketing and visitor-facing sheets use. `surface` is the dashboard's light
// card — the owner tools (rental agreement, plan offer, a roster member) live
// on `--background`, and before this they each kept their own centred Dialog
// rather than go dark, which is how three of them ended up as cramped boxes
// floating in the middle of a phone screen.
//
// Pass `className` for per-sheet width or padding, never for the anchoring and
// never for the tone.

const BottomSheet = DialogPrimitive.Root
const BottomSheetTrigger = DialogPrimitive.Trigger
const BottomSheetClose = DialogPrimitive.Close
const BottomSheetTitle = DialogPrimitive.Title
const BottomSheetDescription = DialogPrimitive.Description

// Keep the field you are typing in where you can see it.
//
// iOS scrolls a focused input into view in the PAGE. It does not do it inside
// a nested scroll area, which every sheet with a form is — so the field you
// tapped ends up flush against the clip edge, or just under it, and typing
// feels like the app is fighting you. The keyboard also arrives after the
// focus event and resizes the web view under us, so one scroll at focus time
// is not enough: the viewport moves again a moment later.
//
// Centring the focused field, then again as the viewport settles, is what
// makes a sheet behave the way every other iOS app does.
function useFocusedFieldInView(ref: React.RefObject<HTMLDivElement | null>) {
  React.useEffect(() => {
    const node = ref.current
    if (!node) return

    let timers: ReturnType<typeof setTimeout>[] = []
    const centre = (el: Element) => {
      if (!el.isConnected) return
      el.scrollIntoView({ block: "center", behavior: "smooth" })
    }

    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null
      if (!el) return
      if (!el.matches("input, textarea, select, [contenteditable='true']")) return
      // Now, and again once the keyboard animation has moved the viewport.
      // 300ms is the length of the iOS keyboard transition.
      timers.forEach(clearTimeout)
      timers = [setTimeout(() => centre(el), 50), setTimeout(() => centre(el), 350)]
    }

    node.addEventListener("focusin", onFocusIn)
    return () => {
      node.removeEventListener("focusin", onFocusIn)
      timers.forEach(clearTimeout)
    }
  }, [ref])
}

function BottomSheetContent({
  className,
  overlayClassName,
  children,
  showHandle = true,
  showCloseButton = true,
  closeDisabled = false,
  tone = "ink",
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  overlayClassName?: string
  showHandle?: boolean
  showCloseButton?: boolean
  /** Blocks the X while a submit is in flight; the overlay is disabled by
      the caller's own onOpenChange. */
  closeDisabled?: boolean
  /** `ink` is the black visitor-facing surface; `surface` is the dashboard's
      light card. Nothing else about the sheet changes between them. */
  tone?: "ink" | "surface" | "inverse"
}) {
  const isInk = tone === "ink"
  // inverse: the opposite of the page. Ink over a light page, white over a
  // dark one — which is bg-foreground text-background by definition, so one
  // set of classes serves both modes with no dark: variants. Children use
  // text-background for ink and bg-background/N for surfaces on it.
  const isInverse = tone === "inverse"
  const contentRef = React.useRef<HTMLDivElement | null>(null)
  useFocusedFieldInView(contentRef)
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          "fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          overlayClassName,
        )}
        style={{
          // Start at the top of what is actually visible, not the top of the
          // layout viewport. On iOS Safari the keyboard scrolls the visual
          // viewport down inside the layout one, and a plain inset-0 overlay
          // stays behind with it — so the page showed through above the sheet
          // the moment a field was focused. --vv-top is 0 until that happens
          // (and everywhere ViewportInsets does not run), which is inset-0.
          top: "var(--vv-top, 0px)",
        }}
      />
      <DialogPrimitive.Content
        ref={contentRef}
        className={cn(
          // Anchored to the bottom edge at EVERY width, desktop included.
          //
          // It used to lift onto a floating card from sm up (bottom-6, all four
          // corners rounded). With a gap under it and a curve on the bottom
          // edge it stopped reading as a sheet that slid up and started reading
          // as a modal someone forgot to centre — the drag handle at the top
          // promises an edge it was no longer touching.
          "fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full max-w-[480px] flex-col",
          "overflow-y-auto overscroll-contain",
          // Top corners only: the bottom edge is the viewport's, so there is
          // nothing there to round.
          "rounded-t-[28px]",
          isInk
            ? "bg-booth-ink text-white ring-1 ring-white/10"
            : isInverse
              ? "bg-foreground text-background ring-1 ring-background/10"
              : "bg-background text-foreground ring-1 ring-border",
          "shadow-[0_-8px_40px_rgba(0,0,0,0.45)]",
          "duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full",
          // The keyboard transition below is inline (it has to name two
          // properties the open/close animation does not touch). This beats it
          // for anyone who asked for less motion — `!` because an inline style
          // outranks a class otherwise.
          "motion-reduce:!transition-none",
          className,
        )}
        style={{
          // Sit ON the keyboard rather than under it.
          //
          // --keyboard-h is published by NativeShell from Capacitor's
          // keyboardWillShow/Hide inside the app shell, and by ViewportInsets
          // from window.visualViewport in a browser (mobile Safari has no
          // keyboard event and does not resize the layout viewport, so the
          // sheet sat behind the keyboard there until the latter existed). It
          // is 0px wherever neither runs. Without it the sheet stays pinned
          // to the viewport's bottom edge while the keyboard covers the lower
          // half of it — which is how the Contact Shop sheet showed its
          // heading and hid the textarea you were typing into.
          bottom: "var(--keyboard-h, 0px)",
          // Ride the keyboard up instead of snapping to meet it.
          //
          // --keyboard-h is measured off visualViewport, and iOS Safari does
          // not reliably report that height WHILE the keyboard animates — it
          // often lands once, at the end. So the sheet sat at bottom:0 with the
          // keyboard climbing over it, then jumped the full height in one
          // frame the moment the number arrived. Low, then suddenly high.
          //
          // 200ms is deliberately shorter than the ~250ms keyboard: where the
          // viewport DOES report continuously, each frame's target is only a
          // few pixels away and the sheet tracks it with an imperceptible
          // trail rather than lagging behind the keys. max-height moves with
          // it so a tall sheet does not re-flow its contents a beat late.
          transition: "bottom 200ms cubic-bezier(0.22, 0.61, 0.36, 1), max-height 200ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          // Inline because the cap has to subtract the keyboard, which no
          // single utility expresses. (The original note here said arbitrary
          // Tailwind values do not build in this project — that was true when
          // it was written and is not any more: `max-h-[92svh]` compiles to a
          // real rule today. The inline style stays for the calc, not for
          // that.) It has to shrink as the keyboard grows or a tall sheet
          // pushes its own top off the screen.
          maxHeight: "calc(92svh - var(--keyboard-h, 0px))",
          // Nothing scrolls to within a thumb's width of the edge. Applies to
          // the sheet's own scrolling and, through inheritance, to the body
          // scrollers the form sheets nest inside it.
          scrollPaddingBlock: "1.25rem",
        }}
        {...props}
      >
        {showHandle && (
          // The affordance that says this dismisses downward.
          <div className="flex justify-center pt-3 pb-1">
            <span className={cn("h-1 w-9 rounded-full", isInk ? "bg-white/20" : isInverse ? "bg-background/20" : "bg-foreground/15")} aria-hidden />
          </div>
        )}

        {showCloseButton && (
          <DialogPrimitive.Close
            disabled={closeDisabled}
            className={cn(
              "absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:opacity-40",
              isInk
                ? "text-white/50 hover:bg-white/5 hover:text-white"
                : isInverse
                  ? "text-background/50 hover:bg-background/10 hover:text-background"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <X className="h-4 w-4" strokeWidth={1.75} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}

        {children}

        {/* The home-indicator inset, ADDED below the caller's own bottom
            padding. It used to be an inline paddingBottom on the sheet, and an
            inline style beats a class — so every sheet's pb-* was silently
            replaced by the inset, which is 0 on desktop. The signup sheet's
            last line ("Already have an account? Sign in") sat flush on the
            bottom edge and read as cut off. */}
        <div aria-hidden className="shrink-0" style={{ height: "env(safe-area-inset-bottom, 0px)" }} />
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetTitle,
  BottomSheetTrigger,
}
